import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { calibrationMatrixApi } from '@/api/calibrationMatrixApi';
import type {
  CalibrationMatrixFilters,
} from '@/api/types/calibrationMatrix.types';

export const calibrationMatrixKeys = {
  all: ['calibrationMatrix'] as const,
  matrix: (filters: CalibrationMatrixFilters) =>
    ['calibrationMatrix', 'matrix', filters] as const,
  export: (filters: CalibrationMatrixFilters) =>
    ['calibrationMatrix', 'export', filters] as const,
  windows: (appraisalId: number | null) =>
    ['calibrationMatrix', 'windows', appraisalId] as const,
};

export function useCalibrationMatrix(filters: CalibrationMatrixFilters) {
  return useQuery({
    queryKey: calibrationMatrixKeys.matrix(filters),
    queryFn: () => calibrationMatrixApi.getMatrix(filters),
    enabled: filters.grade !== null && filters.analytics !== null,
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
  });
}

export function useExportCalibrationMatrix() {
  return useMutation({
    mutationFn: (filters: CalibrationMatrixFilters) =>
      calibrationMatrixApi.exportToExcel(filters),
    onSuccess: ({ blob, fileName }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });
}

export function useCalibrationMatrixAppraisalWindows(appraisalId: number | null) {
  return useQuery({
    queryKey: calibrationMatrixKeys.windows(appraisalId),
    queryFn: () => calibrationMatrixApi.getAppraisalWindows(appraisalId as number),
    enabled: appraisalId !== null,
    staleTime: Infinity,
  });
}
